<template>
  <ListItem :has-error="hasError">
    <div class="handle cursor-grab">
      <Icon name="bars-3" class="text-black/20" />
    </div>
    <ListItemColumn
      v-for="key in Object.keys(modifier.conditions).sort((a, b) => (a < b ? -1 : 1))"
      :key="key"
      :label="t('pricingModifierConditions.' + key)"
    >
      {{ printCondition(key as keyof PricingModifierConditionsDto) }}
    </ListItemColumn>
    <ListItemColumn :label="t('modifier')">
      {{ formatFloat(modifier.factor, 4) }}
    </ListItemColumn>
    <DeleteButton @click.stop="$emit('delete')" />
  </ListItem>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { useMakes } from '@/queries/use-makes.ts';
import { useStationsWithQuery } from '@/queries/use-stations.ts';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data.ts';
import { getLocalizedField } from '@/utils/get-localized-field.ts';
import { formatFloat } from '@/utils/format-numbers.ts';
import ListItem from '@/views/yield-management/ui/ListItem.vue';
import ListItemColumn from '@/views/yield-management/ui/ListItemColumn.vue';
import DeleteButton from '@/views/yield-management/ui/DeleteButton.vue';
import type { PricingModifierConditionsDto, PricingModifierDto } from '@carvia/ros-client-types';

const { modifier } = defineProps<{
  modifier: PricingModifierDto;
  hasError?: boolean;
}>();

defineEmits<{
  (e: 'delete'): void;
}>();

const { t, locale } = useI18n();

const makesResult = useMakes();
const stationsResult = useStationsWithQuery({
  limit: 500,
});
const stations = useFlattenPaginatedData(stationsResult.data);

const printCondition = (conditionKey: keyof PricingModifierConditionsDto) => {
  if (conditionKey === 'timePeriod') {
    if (!modifier.conditions.timePeriod) {
      return '-';
    }
    const fromDay = modifier.conditions.timePeriod.fromDay;
    const fromMonth = formatDate(
      new Date(2025, (modifier.conditions.timePeriod.fromMonth ?? 1) - 1),
      {
        month: 'long',
      },
    );
    const fromYear = !modifier.conditions.timePeriod.isYearly
      ? modifier.conditions.timePeriod.fromYear
      : undefined;
    const toDay = modifier.conditions.timePeriod.toDay;
    const toMonth = formatDate(new Date(2025, (modifier.conditions.timePeriod.toMonth ?? 1) - 1), {
      month: 'long',
    });
    const toYear = fromYear
      ? modifier.conditions.timePeriod.fromMonth <= modifier.conditions.timePeriod.toMonth
        ? fromYear
        : fromYear + 1
      : undefined;
    const fromLabel = fromDay ? `${fromDay}. ${fromMonth}` : fromMonth;
    const toLabel = toDay ? `${toDay}. ${toMonth}` : toMonth;
    return !fromYear
      ? `${fromLabel} - ${toLabel} (${t('yearly')})`
      : `${fromLabel} ${fromYear} - ${toLabel} ${toYear}`;
  }
  if (conditionKey === 'stations') {
    return (
      stations.value
        .filter((s) => modifier.conditions[conditionKey]?.includes(s.id))
        .map((s) => getLocalizedField(s.info, locale.value))
        .join(', ') ?? '-'
    );
  }
  if (conditionKey === 'makes') {
    return (
      makesResult.data.value
        ?.filter((m) => modifier.conditions[conditionKey]?.includes(m.id))
        .map((m) => m.name)
        .join(', ') ?? '-'
    );
  }
  if (conditionKey === 'weekDays') {
    return (
      modifier.conditions[conditionKey]
        ?.map((wd) => formatDate(new Date(2025, 2, 2 + wd), { weekday: 'long' }))
        .join(', ') ?? '-'
    );
  }
  if (conditionKey === 'categories') {
    return modifier.conditions[conditionKey]?.map((c) => t('categories.' + c)).join(', ') ?? '-';
  }
  if (conditionKey === 'fromStationLoad') {
    return modifier.conditions[conditionKey] ? formatFloat(modifier.conditions[conditionKey]) : '-';
  }
  return modifier.conditions[conditionKey] ?? '-';
};
</script>

<i18n lang="json">
{
  "en": {
    "modifier": "Modifier",
    "yearly": "yearly"
  },
  "de": {
    "modifier": "Modifier",
    "yearly": "jährlich"
  },
  "es": {
    "modifier": "Modificador",
    "yearly": "anual"
  }
}
</i18n>
